/**
 * 统一 diff（每个文件的 patch）解析：
 * - parsePatch：逐行（带新旧行号），供手机的统一 diff 视图使用
 * - toSides：从 hunk 还原左右两侧文本（只有 hunk 覆盖的部分），供桌面 Monaco diff 使用
 * patch 可能带 "diff --git" / "---" / "+++" 等头部，从第一个 @@ 开始解析。
 */

export interface DiffLine {
  kind: "hunk" | "add" | "del" | "ctx";
  text: string;
  oldNo: number | null;
  newNo: number | null;
}

const HUNK = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@(.*)$/;

export function parsePatch(patch: string | null): DiffLine[] {
  if (!patch) return [];
  const lines = patch.replace(/\r\n/g, "\n").split("\n");
  const start = lines.findIndex((l) => l.startsWith("@@"));
  if (start < 0) return [];
  const out: DiffLine[] = [];
  let o = 0;
  let n = 0;
  for (const l of lines.slice(start)) {
    const m = HUNK.exec(l);
    if (m) {
      o = Number(m[1]);
      n = Number(m[2]);
      out.push({ kind: "hunk", text: l, oldNo: null, newNo: null });
    } else if (l.startsWith("+")) out.push({ kind: "add", text: l.slice(1), oldNo: null, newNo: n++ });
    else if (l.startsWith("-")) out.push({ kind: "del", text: l.slice(1), oldNo: o++, newNo: null });
    else if (l.startsWith(" ")) out.push({ kind: "ctx", text: l.slice(1), oldNo: o++, newNo: n++ });
    // "\ No newline at end of file" 与末尾空行忽略
  }
  return out;
}

/** 左右两侧文本；hunk 之间用空行隔开，并尽量按行号对齐（最多补 3 行） */
export function toSides(patch: string | null): { original: string; modified: string } {
  const lines = parsePatch(patch);
  const left: string[] = [];
  const right: string[] = [];
  let first = true;
  for (const l of lines) {
    if (l.kind === "hunk") {
      if (!first) {
        left.push("");
        right.push("");
      }
      first = false;
      continue;
    }
    if (l.kind !== "add") left.push(l.text);
    if (l.kind !== "del") right.push(l.text);
  }
  return { original: left.join("\n"), modified: right.join("\n") };
}

export function languageOf(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  return ({ ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript", json: "json", md: "markdown", css: "css", html: "html", py: "python", go: "go" } as Record<string, string>)[ext] ?? "plaintext";
}
